import React, { useContext, useEffect } from 'react';
import { Text, Divider } from '@oneaudi/unified-web-components';
import { useMediaQuery } from '@oneaudi/feature-app-utils';
import { SYS_SPACE_FIXED_100 } from '@oneaudi/unified-web-common';
import styled, { css } from 'styled-components';
import { LinkWithCurrentPage, SUBMENUID, TRIGGERID } from '../../../../ContentType';
import { Context } from '../../../../context/Context';
import {
  LinkList,
  LinkListItem,
  LinkListLink,
  MobileMenuButtonContainer,
  MobileMenuContainer,
  MobileMenuWrapper,
  UnderlinedText,
  MobileMenuButtonWrapper,
  MobileMenuButton,
} from '../../SubPageNavigationStyles';
import { useTrackingManager } from '../../../../utils/useTrackingManager';
import VtpButtons from '../../ui/VtpButtons';
import { attachVerticalNavigation } from '../../../../utils/a11y';
import { useI18n } from '../../../../i18n';
import { useBreakpoint } from '../../../../utils/useBreakpoint';

const StyledText = styled(Text)<{ isAlpha: boolean }>`
  ${({ theme, isAlpha }) =>
    isAlpha &&
    css`
      color: ${theme.colors.currentPage};
    `}
`;

const MobileMenu: React.FC = () => {
  const {
    content: { cta, links, secondaryCta },
    hasCta,
    renderPrimaryButton,
    renderVtpButtons,
    renderSecondaryButton,
    submenuMobileRef,
    showSubmenu,
  } = useContext(Context);
  const secondaryButton = secondaryCta?.customCta;
  const { click } = useTrackingManager();
  const ariaSubmenu = useI18n('ariaSubmenu');
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { breakpoint } = useBreakpoint();

  const renderLink = (
    { text, href, currentPage, ariaLabel, opensInNewTab }: LinkWithCurrentPage,
    index: number,
  ) => {
    const fontWeight = !process.env.IS_LEGACY && currentPage ? 'bold' : 'normal';
    const isAlpha = !process.env.IS_LEGACY;

    return (
      <LinkListItem key={text}>
        <LinkListLink
          href={href}
          target={opensInNewTab ? '_blank' : '_self'}
          onClick={() => click('link', text, href, `${index + 1}`, breakpoint, '')}
          aria-current={currentPage ? 'page' : undefined}
          aria-label={ariaLabel || undefined}
          variant="standalone"
          size={isMobile ? 'sm' : 'md'}
        >
          <StyledText
            tag="span"
            variant={{ type: 'copy', order: '2', style: fontWeight }}
            isAlpha={isAlpha && currentPage!}
          >
            {!isAlpha && currentPage ? <UnderlinedText>{text}</UnderlinedText> : text}
          </StyledText>
        </LinkListLink>
      </LinkListItem>
    );
  };

  useEffect(() => {
    if (!submenuMobileRef.current) return undefined;
    const cleanup = attachVerticalNavigation(submenuMobileRef.current);

    return () => cleanup();
  }, [submenuMobileRef]);

  return (
    <MobileMenuContainer>
      <MobileMenuWrapper
        ref={submenuMobileRef}
        className={showSubmenu ? 'show' : undefined}
        id={SUBMENUID.MOBILE}
        aria-label={ariaSubmenu}
        aria-labelledby={TRIGGERID.MOBILE}
      >
        {/* Link list / subpages */}
        {links && links.length > 0 && (
          <LinkList>{links.map((link, index) => renderLink(link, index))}</LinkList>
        )}
        {/* Cta section */}
        {hasCta && (
          <MobileMenuButtonWrapper>
            <MobileMenuButtonContainer>
              <Divider aria-hidden="true" margin={[0, 0, 'var(--spacing-relative-xl)', 0]} />
              {renderPrimaryButton && (
                <MobileMenuButton
                  href={cta[0].href}
                  {...(cta[0]?.ariaLabel ? { 'aria-label': cta[0].ariaLabel } : undefined)}
                  target={cta[0].opensInNewTab ? '_blank' : '_self'}
                  variant="primary"
                  isFullWidth
                  size="sm"
                  onClick={() => click('cta', cta[0].text, cta[0].href, '', breakpoint, 'primary')}
                >
                  {cta[0].text}
                </MobileMenuButton>
              )}
              {renderVtpButtons && <VtpButtons />}
              {renderSecondaryButton && (
                <MobileMenuButton
                  href={secondaryButton.href}
                  {...(cta[0]?.ariaLabel ? { 'aria-label': cta[0].ariaLabel } : undefined)}
                  variant="secondary"
                  isFullWidth
                  size="sm"
                  onClick={() =>
                    click(
                      'cta',
                      secondaryButton.text,
                      secondaryButton.href,
                      '',
                      breakpoint,
                      'secondary',
                    )
                  }
                  margin={[renderPrimaryButton ? SYS_SPACE_FIXED_100 : 0, 0, 0, 0]}
                >
                  {secondaryButton.text}
                </MobileMenuButton>
              )}
            </MobileMenuButtonContainer>
          </MobileMenuButtonWrapper>
        )}
      </MobileMenuWrapper>
    </MobileMenuContainer>
  );
};

export default MobileMenu;
